import { useCallback } from 'react';

import type { Me } from '@/api/types';

import { useAuth } from './auth-context';

export interface ScopeTarget {
  departmentId?: string | null;
  siteId?: string | null;
}

/**
 * La permission est-elle accordée (dans le service/site indiqué, s'il est donné) ?
 * Reflète la logique du serveur, qui reste la seule autorité : l'interface ne fait
 * que masquer ce qui serait refusé.
 */
export function hasPermission(user: Me | null, code: string, target: ScopeTarget = {}): boolean {
  if (!user || !user.permissions.includes(code)) return false;
  const { departmentId, siteId } = target;
  if (!departmentId && !siteId) return true;
  const scope = user.scopes[code];
  if (!scope) return false;
  if (scope.all_sites) return true;
  if (departmentId && scope.department_ids.includes(departmentId)) return true;
  return Boolean(siteId && scope.site_ids.includes(siteId));
}

export function usePermissions() {
  const { user } = useAuth();
  const can = useCallback(
    (code: string, target?: ScopeTarget) => hasPermission(user, code, target),
    [user],
  );
  const canAny = useCallback(
    (codes: string[]) => codes.some((code) => hasPermission(user, code)),
    [user],
  );
  return { can, canAny, user };
}
